/**
 * fixIndent — language-aware indent repair for the Code node editor.
 *
 * sl-93dd.5 follow-up: `dedentCode` alone is often a no-op — as soon as
 * one line starts at column 0 the common prefix is empty, and the
 * Format button disables even though other lines still have a rogue
 * leading indent. The Python case is the one users hit in practice:
 * a multi-line paste where only the first line is at col 0 and the
 * remainder is indented, producing a real IndentationError.
 *
 * Two strategies are combined under one entry point:
 *
 *   - Python: per-line analysis matching the syntax linter's
 *     `isTopLevelIndentError` heuristic — strip leading whitespace from
 *     any line whose previous non-empty, non-comment line is NOT a
 *     block opener (`:`-terminator). Lines inside `def`, `if`, `for`
 *     etc. keep their indent. Falls back to `dedentCode` when no
 *     rogue indent is found so the button still collapses a shared
 *     prefix (Markdown paste that managed to uniformly indent every
 *     line).
 *
 *   - JavaScript / JSON: `dedentCode` only. JS tolerates top-level
 *     indent, so stripping per-line would destroy intentional inner
 *     structure.
 *
 * Returns the input unchanged when no repair is needed so callers can
 * rely on referential-equality checks to disable UI elements.
 */

import { dedentCode } from './dedent';

export type FixIndentLanguage = 'javascript' | 'python' | 'json';

export function fixIndent(code: string, language: FixIndentLanguage): string {
  if (!code) return code;
  if (language === 'python') {
    const stripped = stripPythonTopLevelIndent(code);
    if (stripped !== code) return stripped;
  }
  return dedentCode(code);
}

/**
 * Walk every line once, tracking the previous non-empty, non-comment
 * line. For every line with leading whitespace whose predecessor is not
 * a block opener, drop the leading whitespace. Block openers are lines
 * whose (comment-stripped) content ends with `:`.
 */
function stripPythonTopLevelIndent(code: string): string {
  const lines = code.split('\n');
  const out: string[] = [];
  let previousContext: string | undefined;

  for (const line of lines) {
    const leading = line.match(/^[ \t]+/)?.[0];
    const trimmed = leading ? line.slice(leading.length) : line;

    if (!leading || leading.length === line.length || trimmed.startsWith('#')) {
      out.push(line);
      if (line.trim().length > 0 && !line.trim().startsWith('#')) {
        previousContext = line;
      }
      continue;
    }

    if (previousContext && isBlockOpener(previousContext)) {
      out.push(line);
      previousContext = line;
      continue;
    }

    out.push(trimmed);
    previousContext = trimmed;
  }

  return out.join('\n');
}

function isBlockOpener(text: string): boolean {
  const stripped = text.replace(/#.*$/, '').trimEnd();
  return stripped.endsWith(':');
}
